import {
  type Cell,
  type Color,
  type FillPattern,
  Workbook,
  type Worksheet,
} from 'exceljs';

import { formatExcelDate } from '../../utils/excel-date';
import type { FarbroBulkDonorScheduleInput } from './schema';

const MAX_HEADER_ROW = 25;
const FARBRO_BREED_MAPPING: Record<string, string> = {
  H: 'HO',
  J: 'JE',
  X: 'XX',
  A: 'AN',
};

const FARBRO_COLUMN_MAPPING = {
  ID: 'donorNameToMatch',
  REG: 'donorRegistrationNumber',
  BDAT: 'birthDate',
  CBRD: 'donorBreed',
  WHERE: 'location',
  OPU: 'opuDate',
  SIREID: 'naab',
  SNAME: 'sire',
  TYPE: 'semenType',
  CCODE: 'collectionCode',
  PRIOR: 'priority',
} as const satisfies Record<string, keyof FarbroBulkDonorScheduleInput>;

const FARBRO_HEADER_NAMES: Record<keyof typeof FARBRO_COLUMN_MAPPING, string> =
  {
    ID: 'ID',
    REG: 'REG',
    BDAT: 'BDAT',
    CBRD: 'CBRD',
    WHERE: 'Where',
    OPU: 'OPU',
    SIREID: 'SireID',
    SNAME: 'Sname',
    TYPE: 'Type',
    CCODE: 'Ccode',
    PRIOR: 'Prior',
  };

const REQUIRED_HEADERS = Object.keys(FARBRO_COLUMN_MAPPING);

export type SourceColor = Pick<Partial<Color>, 'argb' | 'theme' | 'tint'>;

export type SourceCellFormatting = {
  pattern: FillPattern['pattern'];
  foregroundColor?: SourceColor;
  backgroundColor?: SourceColor;
};

export type FarbroRosterSourceRow = {
  rowNumber: number;
  formatting: Partial<
    Record<keyof FarbroBulkDonorScheduleInput, SourceCellFormatting>
  >;
};

export type ParsedFarbroRoster = {
  rows: FarbroBulkDonorScheduleInput[];
  sourceRows: FarbroRosterSourceRow[];
};

export async function parseFarbroRoster(
  file: File,
): Promise<ParsedFarbroRoster> {
  if (!file.name.toLowerCase().endsWith('.xlsx')) {
    throw new Error('FARBRO rosters must be XLSX files.');
  }

  const workbook = new Workbook();
  await workbook.xlsx.load(await file.arrayBuffer());

  const worksheet = workbook.getWorksheet(1);
  if (!worksheet) {
    throw new Error('No worksheet found.');
  }

  const header = findHeaderRow(worksheet);
  const missingHeaders = REQUIRED_HEADERS.filter(
    (requiredHeader) => !header.columns.has(requiredHeader),
  );
  if (missingHeaders.length > 0) {
    throw new Error(
      `Missing required FARBRO columns: ${missingHeaders
        .map(
          (header) =>
            FARBRO_HEADER_NAMES[header as keyof typeof FARBRO_COLUMN_MAPPING],
        )
        .join(', ')}`,
    );
  }

  const rows: FarbroBulkDonorScheduleInput[] = [];
  const sourceRows: FarbroRosterSourceRow[] = [];

  for (
    let rowNumber = header.rowNumber + 1;
    rowNumber <= worksheet.rowCount;
    rowNumber++
  ) {
    const sourceRow = worksheet.getRow(rowNumber);
    const row: Partial<FarbroBulkDonorScheduleInput> = {};
    const formatting: FarbroRosterSourceRow['formatting'] = {};
    let hasData = false;

    for (const [sourceHeader, field] of Object.entries(FARBRO_COLUMN_MAPPING)) {
      const columnNumber = header.columns.get(sourceHeader)!;
      const cell = sourceRow.getCell(columnNumber);
      const value = readCellValue(cell, field);

      if (value !== undefined) {
        row[field] = value as never;
        hasData = true;
      }

      const cellFormatting = readCellFormatting(cell);
      if (cellFormatting) {
        formatting[field] = cellFormatting;
      }
    }

    row.gradeInstruction = getGradeInstruction(formatting.priority);
    const sireMatingClassification = getMatingClassification(formatting.sire);
    const typeMatingClassification = getMatingClassification(
      formatting.semenType,
    );
    if (sireMatingClassification !== typeMatingClassification) {
      throw new Error(
        `Conflicting FARBRO mating colors on spreadsheet row ${rowNumber}.`,
      );
    }
    row.matingClassification = sireMatingClassification;

    if (hasData) {
      rows.push(row as FarbroBulkDonorScheduleInput);
      sourceRows.push({ rowNumber, formatting });
    }
  }

  return { rows, sourceRows };
}

function findHeaderRow(worksheet: Worksheet) {
  let bestMatch = {
    rowNumber: 1,
    columns: new Map<string, number>(),
  };

  const lastCandidateRow = Math.min(worksheet.rowCount, MAX_HEADER_ROW);
  for (let rowNumber = 1; rowNumber <= lastCandidateRow; rowNumber++) {
    const columns = new Map<string, number>();
    worksheet
      .getRow(rowNumber)
      .eachCell({ includeEmpty: true }, (cell, col) => {
        const header = normalizeHeader(cell.text);
        if (header in FARBRO_COLUMN_MAPPING) {
          columns.set(header, col);
        }
      });

    if (columns.size > bestMatch.columns.size) {
      bestMatch = { rowNumber, columns };
    }
    if (columns.size === REQUIRED_HEADERS.length) {
      return bestMatch;
    }
  }

  return bestMatch;
}

function normalizeHeader(value: string) {
  return value.trim().replace(/\s+/g, '').toUpperCase();
}

function readCellValue(
  cell: Cell,
  field: keyof FarbroBulkDonorScheduleInput,
): string | undefined {
  const displayedValue = readDisplayedValue(cell);
  if (
    cell.value === null ||
    cell.value === undefined ||
    displayedValue === ''
  ) {
    return undefined;
  }

  if (field === 'birthDate' || field === 'opuDate') {
    if (cell.value instanceof Date) {
      return formatExcelDate(cell.value);
    }
  }

  if (field === 'donorBreed') {
    const breed = displayedValue.toUpperCase();
    return FARBRO_BREED_MAPPING[breed] ?? breed;
  }

  if (field === 'semenType' && displayedValue.toUpperCase() === 'SEXED') {
    return 'SF F';
  }

  if (field === 'sire' && displayedValue.toUpperCase() === 'GENSRC') {
    return undefined;
  }

  return displayedValue;
}

function readDisplayedValue(cell: Cell) {
  if (typeof cell.value === 'number' && /^0+$/.test(cell.numFmt)) {
    return String(cell.value).padStart(cell.numFmt.length, '0');
  }
  return cell.text.trim();
}

function readCellFormatting(cell: Cell): SourceCellFormatting | undefined {
  const fill = cell.fill;
  if (!fill || fill.type !== 'pattern' || fill.pattern === 'none') {
    return undefined;
  }

  return {
    pattern: fill.pattern,
    foregroundColor: copyColor(fill.fgColor),
    backgroundColor: copyColor(fill.bgColor),
  };
}

function getGradeInstruction(
  formatting: SourceCellFormatting | undefined,
): FarbroBulkDonorScheduleInput['gradeInstruction'] | undefined {
  switch (formatting?.foregroundColor?.argb?.toUpperCase()) {
    case 'FF66FFFF':
    case 'FF00FFFF':
      return 'g1-g2';
    case 'FF00B0F0':
      return 'g1-g2-g3';
    case 'FFFFFF00':
      return 'freeze-dt-g1-g2';
    default:
      return undefined;
  }
}

function getMatingClassification(
  formatting: SourceCellFormatting | undefined,
): FarbroBulkDonorScheduleInput['matingClassification'] | undefined {
  const color = formatting?.foregroundColor;
  switch (color?.argb?.toUpperCase()) {
    case 'FF66FF33':
      return 'angus';
    case 'FFFFFFFF':
      return 'holstein';
    case 'FFD9D9D9':
      return 'crossbred';
    case 'FFFFC000':
      return 'jersey';
    default:
      return color?.theme === 0
        ? (color.tint ?? 0) < 0
          ? 'crossbred'
          : 'holstein'
        : undefined;
  }
}

function copyColor(color: Partial<Color> | undefined): SourceColor | undefined {
  if (!color?.argb && color?.theme === undefined) {
    return undefined;
  }
  return { argb: color.argb, theme: color.theme, tint: color.tint };
}
